import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Alert, Button, Dropdown, Input, Modal, Space, Table, Tag, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import olreadiaService from "services/olreadiaService";
import { useAuth } from "contexts/AuthContext";
import { formatText, toMessage } from "./utils";
import { toast } from "react-toastify";
import RoleDrawer from "./RoleDrawer";
import RoleDetailsDrawer from "./RoleDetailsDrawer";

export default function ProfessionRolesPage() {
  const { user } = useAuth();
  const actorId = user?.id || "";
  const [roles, setRoles] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const fetchRoles = useCallback(async (params = {}) => {
    setFetching(true);
    setError(null);
    try {
      const res = await olreadiaService.professionRoles.list(params);
      const sorted = (Array.isArray(res) ? res : []).sort((a, b) => (b.id || 0) - (a.id || 0));
      setRoles(sorted);
    } catch (err) {
      setError(toMessage(err, "Failed to load profession roles"));
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim()) {
        fetchRoles({ search: query.trim() });
      } else {
        fetchRoles();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, fetchRoles]);

  const filteredRoles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return roles;
    return (roles || []).filter((item) => {
      const search = [
        item?.title,
        item?.description,
        Array.isArray(item?.synonyms) ? item.synonyms.join(", ") : "",
      ]
        .join(" ")
        .toLowerCase();
      return search.includes(q);
    });
  }, [roles, query]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editing?.id) {
        await olreadiaService.professionRoles.update(editing.id, values, actorId);
        toast.success("Profession role updated");
      } else {
        await olreadiaService.professionRoles.create(values, actorId);
        toast.success("Profession role created");
      }
      setDrawerOpen(false);
      await fetchRoles();
    } catch (err) {
      toast.error(toMessage(err, "Failed to save profession role"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (record) => {
    setRecordToDelete(record);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;
    setDeleting(true);
    try {
      await olreadiaService.professionRoles.delete(recordToDelete.id, actorId);
      toast.success("Profession role removed");
      await fetchRoles();
    } catch (err) {
      toast.error(toMessage(err, "Failed to delete profession role"));
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (value) => (
        <Tooltip title={value || "-"} placement="topLeft">
          <span style={{ fontWeight: 600 }}>{formatText(value, 40)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (value) => {
        const text = formatText(value, 50);
        return (
          <Tooltip title={value || "-"} placement="topLeft">
            <span>{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Synonyms",
      key: "synonyms",
      ellipsis: true,
      render: (_, record) => {
        const synonyms = Array.isArray(record?.synonyms) ? record.synonyms : [];
        const text = synonyms.length ? synonyms.slice(0, 3).join(", ") : "-";
        const display = formatText(text, 40);
        return (
          <Tooltip title={text || "-"} placement="topLeft">
            <span>{display}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: () => <Tag color="green">ACTIVE</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <div className="prof-jobs-actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "view",
                  label: "View",
                  icon: <EyeOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setSelected(record);
                    setDetailsOpen(true);
                  },
                },
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setEditing(record);
                    setDrawerOpen(true);
                  },
                },
                {
                  key: "delete",
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleDelete(record);
                  },
                },
              ],
            }}
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="user-tie"
          title="Profession Roles"
          subtitle="Manage professions and role definitions used across Olreadia."
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchRoles} loading={fetching}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            New Role
          </Button>
        </Space>
      </div>

      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 8 }}>
        <Input.Search
          allowClear
          placeholder="Search by title, description, or synonyms"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: "100%", maxWidth: 320 }}
        />
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={<Button size="small" onClick={fetchRoles}>Retry</Button>}
        />
      ) : null}

      <Table
        rowKey="id"
        loading={fetching}
        columns={columns}
        dataSource={filteredRoles}
        className="custom-table"
        bordered
        tableLayout="fixed"
        scroll={{ x: "max-content" }}
        size="small"
        locale={{ emptyText: "No profession roles found." }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e?.target?.closest?.(".prof-jobs-actions")) return;
            setSelected(record);
            setDetailsOpen(true);
          },
        })}
      />

      <RoleDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        loading={submitting}
        initialValues={editing}
      />

      <RoleDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        role={selected}
        onEdit={(record) => {
          setDetailsOpen(false);
          setEditing(record);
          setDrawerOpen(true);
        }}
      />

      <Modal
        open={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        title="Remove profession role?"
        okText="Remove"
        okButtonProps={{ danger: true, loading: deleting }}
        onOk={confirmDelete}
        zIndex={2000}
      >
        <p>This will permanently delete the profession role.</p>
      </Modal>
    </div>
  );
}
